/**
 * Helpers for Web Awesome dialogs and drawers.
 *
 * Web Awesome has no show()/hide() methods: visibility is driven by the `open` property, and every
 * close (user action or programmatic `open = false`) goes through the cancellable `wa-hide` event
 * whose `detail.source` is an element. Shoelace instead emitted `sl-request-close` only for user
 * actions, with `detail.source` being 'close-button' | 'keyboard' | 'overlay', and the app's dialog
 * handlers are written against those semantics. This module re-creates them:
 *
 * - `wa-request-close` is dispatched (bubbling, composed, cancelable) on wa-dialog / wa-drawer for
 *   user-initiated closes only, with the Shoelace-style string `detail.source`. Cancelling it keeps
 *   the element open.
 * - hideElement() / hideAndWait() replace the removed hide() method (hideAndWait resolves after
 *   `wa-after-hide`, like Shoelace's promise-returning hide()).
 * - Open dialogs stay above drawers. Shoelace stacked them with z-index (dialog 1001, drawer 700);
 *   Web Awesome opens both with showModal(), i.e. in the browser top layer, where z-index is
 *   ignored and whichever opened last is on top. Most drawers open ~100 ms after mount, so a dialog
 *   opened by the same action ended up under the drawer and inert. A dialog with
 *   `data-below-drawers` is left alone, for dialogs that open drawers on top of themselves.
 */

// Elements whose `wa-hide` is currently being dispatched or animated.
const closing = new WeakSet()

// Web Awesome closes synchronously inside the user event that triggered it (Escape keydown, or
// pointerdown on the backdrop with light-dismiss), so a close while that event is still being
// dispatched is a user close. The event is kept rather than a flag cleared in a microtask: for
// real input the browser runs a microtask checkpoint after every listener, which would clear the
// flag before Web Awesome's own listener runs. Close buttons need no tracking, see `kind` below.
let userEvent = null
let userSource = null
for (const [type, source] of [
  ['keydown', 'keyboard'],
  ['pointerdown', 'overlay']
]) {
  document.addEventListener(
    type,
    (event) => {
      if (type === 'keydown' && event.key !== 'Escape') return
      userEvent = event
      userSource = source
    },
    true
  )
}

document.addEventListener(
  'wa-hide',
  (event) => {
    const el = event.target
    closing.add(el)
    // dispatchEvent is synchronous, so by the next microtask every listener has run and
    // defaultPrevented is final.
    queueMicrotask(() => {
      if (event.defaultPrevented) closing.delete(el)
    })

    if (el.localName !== 'wa-dialog' && el.localName !== 'wa-drawer') return
    const source = event.detail?.source
    // Web Awesome passes the close button (or any [data-dialog="close"] element) for button
    // closes and its internal <dialog> for Escape, backdrop clicks and programmatic closes.
    let kind = null
    if (source && source.tagName !== 'DIALOG') {
      kind = 'close-button'
    } else if (userEvent && userEvent.eventPhase !== Event.NONE) {
      kind = userSource
      // Web Awesome closes one element per user event; a later close in the same dispatch is the
      // app reacting in code (e.g. a nested dialog's handler closing its drawer).
      userEvent = null
    }
    if (!kind) return // programmatic close: Shoelace did not emit sl-request-close for these
    const request = new CustomEvent('wa-request-close', {
      bubbles: true,
      composed: true,
      cancelable: true,
      detail: { source: kind }
    })
    el.dispatchEvent(request)
    if (request.defaultPrevented) event.preventDefault()
  },
  true
)
document.addEventListener('wa-after-hide', (event) => closing.delete(event.target), true)

// Open wa-dialog elements, in the order they were opened.
const openDialogs = []

function raiseDialog(el) {
  const native = el.shadowRoot?.querySelector('dialog')
  if (!native?.open || closing.has(el) || el.hasAttribute('data-below-drawers')) return
  const focused = el.contains(document.activeElement) ? document.activeElement : null
  // Re-entering the top layer is the only way to move up. Web Awesome's dialog does not listen to
  // the native `close` event, so this starts no hide, and without a layout in between its
  // visibility watcher sees no change.
  native.close()
  native.showModal()
  focused?.focus()
  // Re-register as the topmost dismissible so Escape closes only the dialog. Removing first also
  // moves its document keydown listener behind the drawer's: the listeners run in the order they
  // were added, and a closing dialog unregisters before later listeners check who is on top.
  el.removeOpenListeners?.()
  el.addOpenListeners?.()
}

// Web Awesome reflects `open` in the same update that calls showModal(), both when `open` changes
// and when an element is created already open, so this runs once the element is in the top layer.
new MutationObserver((records) => {
  let drawerOpened = false
  for (const { target } of records) {
    if (target.localName === 'wa-dialog') {
      const index = openDialogs.indexOf(target)
      if (index !== -1) openDialogs.splice(index, 1)
      if (target.hasAttribute('open')) openDialogs.push(target)
    } else if (target.localName === 'wa-drawer' && target.hasAttribute('open')) {
      drawerOpened = true
    }
  }
  // dialogs removed from the page while open never lose their `open` attribute
  for (let i = openDialogs.length - 1; i >= 0; i--) {
    if (!openDialogs[i].isConnected) openDialogs.splice(i, 1)
  }
  if (drawerOpened) openDialogs.forEach(raiseDialog)
}).observe(document.documentElement, { subtree: true, attributeFilter: ['open'] })

/**
 * Close a wa-dialog / wa-drawer / wa-dropdown unless it is already closing.
 * Safe to call from inside the element's own `wa-request-close` / `wa-hide` handler.
 */
export function hideElement(el) {
  if (el && el.open && !closing.has(el)) el.open = false
}

/**
 * Close the element and resolve once the hide animation finished (`wa-after-hide`).
 */
export function hideAndWait(el) {
  return new Promise((resolve) => {
    if (!el || (!el.open && !closing.has(el))) return resolve()
    const onAfterHide = (event) => {
      if (event.target !== el) return
      el.removeEventListener('wa-after-hide', onAfterHide)
      resolve()
    }
    el.addEventListener('wa-after-hide', onAfterHide)
    hideElement(el)
  })
}
